import React, { useMemo, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Stack,
  Typography,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import { AccountCircle, Logout } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import LoginModal from '../modals/LoginModal';
import { useNavigate } from 'react-router-dom';
import EditVendorProfile from '../modals/EditVendorProfile';
import offMarketsLogoSvgSrc from '../assets/OffMarketsLogoTransparent.svg';

export default function Header() {
  const { currentUser, userProfile, logout, loading: authLoading } = useAuth();
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editVendorProfileOpen, setEditVendorProfileOpen] = useState(false);
  const navigate = useNavigate();

  const normalizedUserType = useMemo(() => {
    const t = (userProfile?.userType || '').toLowerCase();
    if (t === 'vendor' || t === 'wholesaler') return 'vendor';
    if (t === 'investor') return 'investor';
    return null;
  }, [userProfile?.userType]);

  const isVendor = Boolean(currentUser && normalizedUserType === 'vendor');
  const vendorId = userProfile?.uid || currentUser?.uid || 'vendor_001';

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      handleProfileMenuClose();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Show loading screen while Firebase initializes
  if (authLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'primary.main',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
        }}
      >
        <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
          OffMarkets
        </Typography>
        <Typography variant="body1" sx={{ color: 'white', opacity: 0.8 }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AppBar position="fixed" sx={{ backgroundColor: 'white', backgroundImage: 'none', boxShadow: 1, pl: 0, pr: 0, py: 0 }}>
        <Toolbar sx={{ justifyContent: 'space-between', backgroundImage: 'none', px: 0, pl: 0, minHeight: 'auto', py: 0, height: 140 }}>
            {/* Logo positioned far left */}
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 0, ml: 0, mr: 0, py: 0, height: '100%' }}>
              <Box
                component="img"
                src={offMarketsLogoSvgSrc}
                alt="OffMarkets Logo"
                sx={{
                  height: 130,
                  width: 'auto',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 },
                  ml: 0,
                  py: 0,
                  objectFit: 'contain'
                }}
                onClick={() => {
                  if (isVendor) navigate('/vendor');
                  else if (currentUser) navigate('/investor');
                  else navigate('/');
                }}
                onError={(e) => {
                  // Fallback to the public path if bundling fails for any reason.
                  const img = e.currentTarget;
                  if (img?.src && img.src.includes('OffMarketsLogo.svg')) return;
                  img.src = '/OffMarketsLogo.png';
                }}
              />
            </Box>

            {/* Navigation and User Menu */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ height: 'auto', py: 0, pr: 1 }}>
              {isVendor ? (
                <>
                  <Button
                    variant="text"
                    onClick={() => navigate('/vendor')}
                    sx={{
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      px: 1,
                      py: 0,
                      minWidth: 'auto',
                      minHeight: 'auto'
                    }}
                  >
                    My Properties
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate(`/vendor/${vendorId}`)}
                    sx={{
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      px: 1,
                      py: 0,
                      minWidth: 'auto',
                      minHeight: 'auto'
                    }}
                  >
                    Storefront
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="text"
                    onClick={() => navigate('/investor')}
                    sx={{
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      px: 1,
                      py: 0,
                      minWidth: 'auto',
                      minHeight: 'auto'
                    }}
                  >
                    For Investors
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => navigate('/wholesaler')}
                    sx={{
                      color: 'text.primary',
                      '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                      px: 1,
                      py: 0,
                      minWidth: 'auto',
                      minHeight: 'auto'
                    }}
                  >
                    For Wholesalers
                  </Button>
                </>
              )}

              {currentUser ? (
                <>
                  <Typography variant="body2" sx={{ color: 'text.secondary', px: 1, py: 0, lineHeight: 1.2 }}>
                    Welcome, {userProfile?.displayName || currentUser.email}
                  </Typography>
                  <IconButton
                    size="medium"
                    aria-label="account of current user"
                    aria-controls="menu-appbar"
                    aria-haspopup="true"
                    onClick={handleProfileMenuOpen}
                    color="inherit"
                    sx={{ px: 0.5, py: 0, minHeight: 'auto' }}
                  >
                    <AccountCircle sx={{ color: 'primary.main' }} />
                  </IconButton>
                  <Menu
                    id="menu-appbar"
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    keepMounted
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'right',
                    }}
                    open={Boolean(anchorEl)}
                    onClose={handleProfileMenuClose}
                  >
                    {isVendor && (
                      <MenuItem
                        onClick={() => {
                          handleProfileMenuClose();
                          setEditVendorProfileOpen(true);
                        }}
                      >
                        Edit Profile
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <Logout sx={{ mr: 1 }} />
                      Logout
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <Button
                  variant="outlined"
                  onClick={() => setLoginModalOpen(true)}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      backgroundColor: 'rgba(26, 54, 93, 0.04)'
                    },
                    px: 3,
                    py: 1.5,
                    minWidth: 120,
                    fontSize: '1.1rem',
                    fontWeight: 500
                  }}
                >
                  Login
                </Button>
              )}
            </Stack>
          </Toolbar>
      </AppBar>
      {/* Spacer to prevent content from being hidden behind fixed header */}
      <Toolbar sx={{ minHeight: '140px !important', height: 140 }} />

      <LoginModal
        open={loginModalOpen}
        onClose={() => setLoginModalOpen(false)}
      />

      {isVendor && (
        <EditVendorProfile
          open={editVendorProfileOpen}
          onClose={() => setEditVendorProfileOpen(false)}
        />
      )}
    </>
  );
}
